import { useState } from "react";
import {
  Text,
  Pressable,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Linking,
} from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useLocalSearchParams, useRouter } from "expo-router";
import styled from "styled-components/native";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/lib/firebase";
import { setItem } from "@/lib/storage";
import { createClub } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";

const MAX_WIDTH = 480;

const Container = styled.View`
  flex: 1;
  background-color: ${colors.white};
`;

const Center = styled.View`
  flex: 1;
  align-items: center;
`;

const Content = styled.View`
  flex: 1;
  padding-horizontal: 24px;
  padding-top: 24px;
  max-width: ${MAX_WIDTH}px;
  width: 100%;
`;

const Title = styled.Text`
  font-size: 24px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
`;

const Subtitle = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  line-height: 22px;
  margin-bottom: 28px;
`;

const Label = styled.Text<{ extraMarginTop?: boolean }>`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  ${({ extraMarginTop }) => (extraMarginTop ? "margin-top: 12px;" : "")}
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  font-size: 16px;
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const ErrorBox = styled.View`
  background-color: ${colors.dangerLight};
  border-radius: ${radius.md}px;
  padding-horizontal: 16px;
  padding-vertical: 12px;
  margin-top: 12px;
  margin-bottom: 4px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 14px;
  font-weight: ${font.semibold};
`;

const BtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const OrDivider = styled.View`
  flex-direction: row;
  align-items: center;
  margin-vertical: 20px;
`;

const OrLine = styled.View`
  flex: 1;
  height: 1px;
  background-color: ${colors.line};
`;

const OrText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  padding-horizontal: 12px;
`;

const SecondaryBtnText = styled.Text`
  color: ${colors.brand};
  font-size: 16px;
  font-weight: ${font.bold};
`;

const SuccessTitle = styled.Text`
  font-size: 24px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  text-align: center;
  margin-bottom: 8px;
`;

const SuccessBody = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  text-align: center;
  line-height: 22px;
`;

const SuccessEmoji = styled.Text`
  font-size: 48px;
  text-align: center;
  margin-bottom: 16px;
`;

const BoldEmail = styled.Text`
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

export default function SignupAccount() {
  const { clubName, teams } =
    useLocalSearchParams<{ clubName: string; teams: string }>();

  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [error, setError] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  let teamsList: string[];
  try {
    teamsList = JSON.parse(teams || '["Team"]');
  } catch {
    teamsList = ["Team"];
  }
  const shortName = (clubName || "Club")
    .split(/\s+/)
    .map((w: string) => w[0])
    .join("")
    .toUpperCase();

  const createClubForUser = async (userId: string, userEmail: string) => {
    const userName = userEmail.split("@")[0] || "";
    await createClub({
      name: clubName || "My Club",
      shortName,
      teams: teamsList,
      userId,
      userEmail,
      userName,
    });
  };

  const handlePassword = async () => {
    if (!email.trim() || !password.trim()) return;
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await createClubForUser(cred.user.uid, email);
      // Auth state change → NavigationGate → has club → app
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists. Try signing in instead.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err.code === "auth/weak-password") {
        setError("Password must be at least 6 characters.");
      } else {
        setError(err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email.trim()) return;

    setMagicLoading(true);
    setError("");
    try {
      const sendMagicLink = httpsCallable(functions, "sendMagicLink");
      await sendMagicLink({
        email,
        type: "signup",
        clubData: { name: clubName, shortName, teams: teamsList },
      });
      setMagicSent(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
    } finally {
      setMagicLoading(false);
    }
  };

  if (magicSent) {
    return (
      <Container>
        <Center>
          <Content style={{ justifyContent: "center", alignItems: "center" }}>
            <SuccessEmoji>✉️</SuccessEmoji>
            <SuccessTitle>Check your email</SuccessTitle>
            <SuccessBody>
              We sent a verification link to{" "}
              <BoldEmail>{email}</BoldEmail>.
              {"\n"}Tap the link to finish setting up your club.
            </SuccessBody>
          </Content>
        </Center>
      </Container>
    );
  }

  return (
    <Container>
      <Center>
        <KeyboardAvoidingView
          style={{ flex: 1, width: "100%", maxWidth: MAX_WIDTH, paddingHorizontal: 24 }}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
        >
          <KeyboardAwareScroll contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
            <Content style={{ paddingHorizontal: 0 }}>
              <Title>Create your account</Title>
              <Subtitle>
                Last step — this is how you'll sign in to manage{" "}
                <Text style={{ fontWeight: "700", color: colors.ink }}>{clubName}</Text>.
              </Subtitle>

              <Label>Email address</Label>
              <Input
                placeholder="you@email.com"
                placeholderTextColor={colors.inkMuted}
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                autoFocus
              />

              {error ? (
                <ErrorBox>
                  <ErrorText>{error}</ErrorText>
                </ErrorBox>
              ) : null}

              <Pressable
                onPress={handleMagicLink}
                disabled={magicLoading}
                style={({ pressed }) => [
                  {
                    backgroundColor: colors.brand,
                    borderRadius: radius.card,
                    paddingVertical: 16,
                    alignItems: "center" as const,
                    marginTop: 20,
                    shadowColor: colors.brand,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.25,
                    shadowRadius: 14,
                    elevation: 4,
                  },
                  pressed && { opacity: 0.9 },
                ]}
              >
                {magicLoading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <BtnText>Send verification link →</BtnText>
                )}
              </Pressable>

              <OrDivider>
                <OrLine />
                <OrText>OR</OrText>
                <OrLine />
              </OrDivider>

              <Label>Password</Label>
              <Input
                placeholder="At least 6 characters"
                placeholderTextColor={colors.inkMuted}
                value={password}
                onChangeText={setPassword}
                secureTextEntry
              />

              <Pressable
                onPress={handlePassword}
                disabled={loading}
                style={({ pressed }) => [
                  {
                    backgroundColor: colors.white,
                    borderWidth: 2,
                    borderColor: colors.line,
                    borderRadius: radius.card,
                    paddingVertical: 16,
                    alignItems: "center" as const,
                    marginTop: 12,
                  },
                  pressed && { borderColor: colors.brand },
                ]}
              >
                {loading ? (
                  <ActivityIndicator color={colors.brand} />
                ) : (
                  <SecondaryBtnText>Create with password instead</SecondaryBtnText>
                )}
              </Pressable>
              <LegalText>
                By creating an account, you agree to our{" "}
                <LegalLink onPress={() => Linking.openURL("https://inthecrease.uk/terms")}>Terms of Service</LegalLink>
                {" "}and{" "}
                <LegalLink onPress={() => Linking.openURL("https://inthecrease.uk/privacy")}>Privacy Policy</LegalLink>.
              </LegalText>
            </Content>
          </KeyboardAwareScroll>
        </KeyboardAvoidingView>

      </Center>
    </Container>
  );
}

const LegalText = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  text-align: center;
  line-height: 18px;
  margin-top: 16px;
`;

const LegalLink = styled.Text`
  color: ${colors.brand};
  font-weight: ${font.semibold};
`;
